/**
 * ProductionImageLightbox — the shared Dialog image viewer for production
 * stations. Lifted from the Film Print station's own lightbox (its only
 * behaviours were already generic) so every station flicks through images the
 * same way.
 *
 * Contract:
 *   - ONE FLAT LIST spanning the whole screen's images. The page decides what
 *     goes in it and in what order; the arrows and Arrow keys ride the list
 *     end to end without wrapping.
 *   - TIERED SOURCES, not loose url strings. Each image carries the same
 *     ordered `{ url, tier }` chain the tile walked, plus the kind it claims
 *     to show. A source's full-size twin (`fullUrl`) is what the view
 *     shows. A load error advances along that chain WITHOUT moving the
 *     flat-list index, and the caption, the alt text and the blurred thumb
 *     placeholder all move with it. A view that fell to the catalogue photo
 *     stops calling itself the back print, exactly as the tile does, because
 *     both read the same label resolver.
 *   - HONEST EXHAUSTION. When every source in a chain has failed the view says
 *     so instead of holding a broken image under a caption naming a tier that
 *     is not on screen.
 *   - PERFORMANCE: only the visible image loads; the two neighbours are
 *     pre-warmed (`warmImage`) so a flick feels instant without downloading
 *     everything up front. This matters on the China link.
 *   - A CLOSE CONTROL AN OPERATOR CAN HIT. 48px and labelled (§4.3), not the
 *     shared dialog's bare 16px glyph: these stations are worked in gloves.
 *   - SCANNER FOCUS (PRODUCTION_DESIGN §6.2): this component owns the close
 *     half of the contract. On every close path (Escape, the close button, a
 *     click outside) it blocks Radix's stale focus restoration and puts focus
 *     back on `[data-scanner-input]` within one animation-frame tick. The PAGE
 *     still owns pausing `useScannerAutoFocus` while `open` is true.
 *   - Station copy (title, description, arrows) is passed in: no station
 *     inherits another's words. The per-image caption is NOT passed in, and
 *     deliberately so: it has to be derived from the source actually rendering
 *     or it will drift from the tile that opened it.
 *
 * Production-app sizing: 48px hit targets on the arrows (PRODUCTION_DESIGN
 * §4.3), keyboard left/right, Esc/click-out via the Dialog primitive.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  buildPreviewSourceChain,
  productionPreviewLabelKey,
  type ProductionPreviewKind,
  type ProductionPreviewSourceRef,
} from './ProductionPreviewTile';
import { warmImage } from '@/lib/image-thumbnail';
import { cn } from '@/lib/utils';

export interface ProductionLightboxImage {
  /**
   * Ordered source chain, tier carried per url. Normally the tile's own
   * entry sources, optionally sliced so the view opens on the source the tile
   * actually rendered, or swapped for full-resolution equivalents.
   */
  sources: ReadonlyArray<ProductionPreviewSourceRef>;
  /** What this view claims to show. Drives the tier-aware caption. */
  kind: ProductionPreviewKind;
  /** What the image is OF: product name, or order number and SKU. */
  subject: string;
  /**
   * Low-resolution placeholder painted behind the full view while it streams
   * in. It belongs to the PREFERRED source only, so it is dropped the moment
   * the chain falls back: a backdrop showing tier one behind a caption naming
   * tier three is the same false claim the tiles exist to prevent.
   */
  thumbUrl?: string | null;
}

/** Station-supplied copy. Required: no station should inherit another's words. */
export interface ProductionLightboxCopy {
  title: string;
  description: string;
  previousLabel: string;
  nextLabel: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: ProductionLightboxImage[];
  index: number;
  onIndexChange: (index: number) => void;
  copy: ProductionLightboxCopy;
}

/** Put the scanner back in charge one tick after the overlay releases focus. */
function refocusScannerInput(): void {
  const focusScanner = (): void => {
    document.querySelector<HTMLElement>('[data-scanner-input]')?.focus();
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(focusScanner);
  else setTimeout(focusScanner, 0);
}

export function ProductionImageLightbox({
  open,
  onOpenChange,
  images,
  index,
  onIndexChange,
  copy,
}: Props) {
  const { t } = useTranslation('production');
  const total = images.length;
  const safeIndex = total > 0 ? Math.min(Math.max(index, 0), total - 1) : 0;
  const current = images[safeIndex];
  const hasMultiple = total > 1;

  // De-duplicated chain for the CURRENT image, through the SAME builder the
  // tiles use, so an identical url collapses once and keeps the tier of its
  // first mention rather than swallowing a load-error step.
  const sources = useMemo(
    () =>
      buildPreviewSourceChain(
        (current?.sources ?? []).map(
          (source) => [source.fullUrl ?? source.url, source.tier] as const,
        ),
      ),
    [current],
  );
  const sourceKey = sources.map((source) => source.url).join('|');

  const [sourceIndex, setSourceIndex] = useState(0);
  // Reset to the preferred source whenever the flat index OR the source list
  // itself changes — a re-signed url must be retried, not written off.
  useEffect(() => {
    setSourceIndex(0);
  }, [safeIndex, sourceKey]);

  const active = sources[sourceIndex];
  const exhausted = sources.length === 0 || sourceIndex >= sources.length;
  // Undefined tier when nothing rendered: the caption falls back to the
  // slot's own plain name, which is still true of the slot.
  const label = t(productionPreviewLabelKey(current?.kind ?? 'product', active?.tier));
  const subject = current?.subject ?? '';
  const caption = subject ? `${subject} · ${label}` : label;
  const alt = subject ? `${subject} ${label}` : label;

  const goPrev = () => {
    if (!hasMultiple) return;
    onIndexChange(Math.max(safeIndex - 1, 0));
  };
  const goNext = () => {
    if (!hasMultiple) return;
    onIndexChange(Math.min(safeIndex + 1, total - 1));
  };

  useEffect(() => {
    if (!open || !hasMultiple) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onIndexChange(Math.max(safeIndex - 1, 0));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onIndexChange(Math.min(safeIndex + 1, total - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hasMultiple, safeIndex, total, onIndexChange]);

  // Pre-warm ONLY the neighbours of the current image, and only their
  // preferred source — instant flicks without fetching the whole list.
  useEffect(() => {
    if (!open) return;
    for (const i of [safeIndex - 1, safeIndex + 1]) {
      const first = images[i]?.sources[0];
      const url = first?.fullUrl ?? first?.url;
      if (url) warmImage(url);
    }
  }, [open, safeIndex, images]);

  if (!current) return null;

  // The thumb belongs to the preferred source. Once the chain has fallen back
  // it is a picture of something else, so it goes. A preferred source that is
  // a downscaled copy is its own thumb, already painted and cached by the tile.
  const first = current.sources[0];
  const backdropThumb =
    sourceIndex === 0 ? (first?.fullUrl ? first.url : current.thumbUrl) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl gap-3"
        // The shared dialog's own close control is a bare 16px glyph. These
        // operators wear gloves and the doc requires 48px (§4.3), so it is
        // replaced below rather than sitting there as a target nobody can hit.
        hideCloseButton
        onCloseAutoFocus={(event) => {
          // Radix would restore focus to whatever opened the dialog — usually a
          // thumbnail that may no longer exist after a refetch. Block it and
          // hand the scanner back instead (PRODUCTION_DESIGN §6.2).
          event.preventDefault();
          refocusScannerInput();
        }}
      >
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <DialogDescription className="sr-only">{copy.description}</DialogDescription>

        <DialogClose
          aria-label={t('preview.close')}
          className={cn(
            'absolute right-3 top-3 z-10 flex h-12 items-center gap-2 rounded-md px-4',
            'bg-background/90 text-base font-medium text-foreground shadow-sm ring-1 ring-border',
            'transition-colors duration-150 motion-reduce:transition-none',
            '[@media(hover:hover)]:hover:bg-accent',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <X className="h-5 w-5" aria-hidden="true" />
          {t('preview.close')}
        </DialogClose>

        <div className="relative flex items-center justify-center">
          {/* key remount per index: the previous image never lingers while the
              next streams in; the thumb serves as the instant placeholder. */}
          <div
            key={safeIndex}
            className="flex max-h-[80vh] min-h-[40vh] w-full items-center justify-center rounded-md bg-muted/30"
            style={backdropThumb ? {
              backgroundImage: `url(${backdropThumb})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            } : undefined}
          >
            {exhausted ? (
              /* Every source failed. Say so, rather than holding a broken
                 image under a caption naming a tier that is not on screen. */
              <div
                className="flex flex-col items-center justify-center gap-3 py-16"
                role="img"
                aria-label={`${label}: ${t('preview.unavailable')}`}
              >
                <ImageOff className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
                <p className="text-base text-muted-foreground">{t('preview.unavailable')}</p>
              </div>
            ) : (
              /* Checkerboard on the IMAGE, not on the container: the container's
                 `background-image` is already spoken for by the blurred-thumb
                 placeholder (inline style), which would silently clobber a
                 class-based backdrop in exactly the case that matters — artwork
                 present. Painting it on the img is also more correct: it is the
                 transparent PNG's own pixels the white ink needs to sit against
                 (BLU-818). object-contain for the same reason as the tiles:
                 cover would crop the neckline and the top of the print. */
              <img
                src={active?.url}
                alt={alt}
                decoding="async"
                onError={() => setSourceIndex((i) => i + 1)}
                className="artwork-checker max-h-[80vh] w-auto max-w-full rounded-md object-contain"
              />
            )}
          </div>

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={goPrev}
                disabled={safeIndex === 0}
                aria-label={copy.previousLabel}
                className={cn(
                  'absolute left-2 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center',
                  'rounded-full bg-background/80 text-foreground shadow-md ring-1 ring-border',
                  'transition-opacity duration-200 disabled:opacity-30 motion-reduce:transition-none',
                  '[@media(hover:hover)]:hover:bg-background',
                )}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={safeIndex === total - 1}
                aria-label={copy.nextLabel}
                className={cn(
                  'absolute right-2 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center',
                  'rounded-full bg-background/80 text-foreground shadow-md ring-1 ring-border',
                  'transition-opacity duration-200 disabled:opacity-30 motion-reduce:transition-none',
                  '[@media(hover:hover)]:hover:bg-background',
                )}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          {/* aria-live so a fallback announces the tier that actually landed. */}
          <p className="text-base text-muted-foreground" aria-live="polite">
            {caption}
          </p>
          {hasMultiple && (
            <span className="shrink-0 text-sm tabular-nums text-muted-foreground" aria-live="polite">
              {safeIndex + 1} / {total}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
